import './_styles.scss';
import './_styles.dark-mode.scss';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import qs from 'qs';

import Loader from '../../../components/Loaders/Loader';
import Table from './components/Table';
import Bot from './components/Table/bot';
import ModalsManager from './components/Modals/Manager';

import {
  getNotificationsList,
  addNotificationStaffEmail,
  deleteNotificationStaffEmail,
  resendNotificationStaffEmailConfirm,
  resetNotification,
  editNotification,
  editNotificationStaffEmail,
  getNotificationView,
  connectTelegramBotNotification,
  checkStatusTelegramBotNotification,
  toggleNotificationStatus,
  editTelegramBotNotification,
} from '../../../services/endpoints/settings';
import { settingsSelector } from '../../../redux/selectors/settings';

import { STATUS_REQUEST_INTERVAL } from './helpers';

let intervalRequest = null;
const Notifications = () => {
  const location = useLocation();

  const selectedItem = useRef(null);

  const queryString = qs.parse(location.search.slice(1));

  const reports = useSelector((state) => settingsSelector(state));
  const { mainConfig: config } = reports;
  const { access, languages } = config;

  const [data, setData] = useState([]);
  const [openModal, setOpenModal] = useState('');
  const [modalError, setModalError] = useState('');
  const [modalSuccess, setModalSuccess] = useState('');
  const [isLoading, setIsLoading] = useState({
    page: true,
    submit: false,
    modal: false,
    modalSubmit: false,
    modalSubmitBot: false,
  });

  const tgBot = useMemo(
    () => data?.notifications_bots?.find((bot) => bot.bot === 1),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.notifications_bots],
  );

  const onHideModals = () => {
    setOpenModal('');
    setIsLoading((prevState) => ({ ...prevState, modal: false, modalSubmit: false }));
    setTimeout(() => {
      selectedItem.current = {};
      setModalError('');
      setModalSuccess('');
    }, 150);
  };

  const clearStatusInterval = () => {
    clearInterval(intervalRequest);
    intervalRequest = null;
  };

  const fetchList = async (loader = true) => {
    loader && setIsLoading((prevState) => ({ ...prevState, page: true }));
    try {
      const response = await getNotificationsList(queryString);
      if (response.success) {
        setData(response.data);
      }
      loader && setIsLoading((prevState) => ({ ...prevState, page: false }));
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }
      loader && setIsLoading((prevState) => ({ ...prevState, page: false }));
    }
  };

  const fetchPreview = async (id, payload) => {
    setIsLoading((prevState) => ({ ...prevState, modal: true }));
    try {
      const response = await getNotificationView(id, payload);
      if (response.success) {
        selectedItem.current = { ...selectedItem.current, preview: response.data?.preview };
      }
      setIsLoading((prevState) => ({ ...prevState, modal: false }));
    } catch (error) {
      setModalError(error?.data?.error_message ?? 'Something went wrong');
      setIsLoading((prevState) => ({ ...prevState, modal: false }));
    }
  };

  const checkBotStatus = async (id) => {
    setIsLoading((prevState) => ({ ...prevState, modalSubmitBot: true }));
    try {
      const response = await checkStatusTelegramBotNotification(id);
      if (response.success) {
        if (response?.data?.webhook_status === 1) {
          setIsLoading((prevState) => ({ ...prevState, modalSubmitBot: false }));
          clearStatusInterval();
          if (openModal === 'bot-telegram' || openModal === 'edit-bot-telegram') onHideModals();
          fetchList();
        }
        if (response?.data?.webhook_status === 2) {
          clearStatusInterval();
          if (openModal === 'bot-telegram' || openModal === 'edit-bot-telegram') {
            if (tgBot) {
              setModalError('Internal error.');
            } else {
              onHideModals();
            }
          }
          setIsLoading((prevState) => ({ ...prevState, modalSubmitBot: false }));
        }
      }
    } catch (error) {
      clearStatusInterval();
      setIsLoading((prevState) => ({ ...prevState, modalSubmitBot: false }));
    }
  };

  const runStatusIntervalChecker = (id) => {
    intervalRequest = setInterval(() => checkBotStatus(id || tgBot?.id), STATUS_REQUEST_INTERVAL);
  };

  const onChangeStatus = async (event, item) => {
    event.persist();
    event.target.disabled = true;
    event?.target?.setAttribute('disabled', 'disabled');

    setData((prevState) => ({
      ...prevState,
      notifications: {
        ...prevState.notifications,
        user: prevState?.notifications?.user?.map((user) => {
          if (user.id === item.id) {
            return {
              ...user,
              is_enabled: !user.is_enabled,
            };
          }
          return user;
        }),
      },
    }));

    try {
      await toggleNotificationStatus(item.id, { status: !item.is_enabled });
    } catch (error) {
      await fetchList(false);
    } finally {
      event.target.disabled = false;
      event?.target?.removeAttribute('disabled');
    }
  };

  const onAction = (type, item) => {
    if (item) {
      selectedItem.current = { ...item, mode: type };
    } else {
      selectedItem.current = { mode: type };
    }

    switch (type) {
    case 'add':
    case 'edit':
      selectedItem.current = { ...selectedItem.current, options: data?.notifications?.staff };
      setOpenModal(type);
      break;

    case 'view-user':
      fetchPreview(item.id);
      setOpenModal(type);
      break;

    case 'view-staff':
      setOpenModal(type);
      break;

    default:
      break;
    }
  };

  const onAddEmail = async (payload) => {
    setIsLoading((prevState) => ({ ...prevState, modalSubmit: true }));
    try {
      await addNotificationStaffEmail(payload);
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
      onHideModals();
      fetchList();
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
      setModalError(error?.data?.error_message ?? 'Something went wrong');
    }
  };

  const onEditEmail = async (payload) => {
    setIsLoading((prevState) => ({ ...prevState, modalSubmit: true }));
    try {
      await editNotificationStaffEmail(selectedItem.current?.id, { notifications: payload?.notifications });
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
      onHideModals();
      fetchList();
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
      setModalError(error?.data?.error_message ?? 'Something went wrong');
    }
  };

  const onDeleteEmail = async () => {
    onHideModals();
    setIsLoading((prevState) => ({ ...prevState, page: true }));
    try {
      await deleteNotificationStaffEmail(selectedItem.current?.id);
      fetchList();
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, page: false }));
    }
  };

  const onResendEmail = async () => {
    setIsLoading((prevState) => ({ ...prevState, modalSubmit: true }));
    setModalError('');
    setModalSuccess('');
    try {
      await resendNotificationStaffEmailConfirm(selectedItem.current?.id);
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
      setModalSuccess('A confirmation email has been sent');
    } catch (error) {
      setModalError(error?.data?.error_message ?? 'Something went wrong');
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
    }
  };

  const onReset = async (payload = {}) => {
    onHideModals();
    setIsLoading((prevState) => ({ ...prevState, page: true }));
    try {
      await resetNotification(selectedItem.current?.id, payload);
      fetchList();
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, page: false }));
    }
  };

  const onEditNotification = async (payload) => {
    setIsLoading((prevState) => ({ ...prevState, modalSubmit: true }));
    try {
      await editNotification(selectedItem.current?.id, payload);
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
      onHideModals();
      fetchList();
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
      setModalError(error?.data?.error_message ?? 'Something went wrong');
    }
  };

  const onAddTGBot = async (payload) => {
    setModalError('');
    setIsLoading((prevState) => ({ ...prevState, modalSubmitBot: true }));
    try {
      const response = await connectTelegramBotNotification(payload);
      runStatusIntervalChecker(response?.data);
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, modalSubmitBot: false }));
      setModalError(error?.data?.error_message ?? 'Something went wrong');
    }
  };

  const onEditGBot = async (payload) => {
    setModalError('');
    setIsLoading((prevState) => ({ ...prevState, modalSubmitBot: true }));
    try {
      if (tgBot) {
        const response = await editTelegramBotNotification(tgBot.id, payload);
        runStatusIntervalChecker(response?.data);
      }
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, modalSubmitBot: false }));
      setModalError(error?.data?.error_message ?? 'Something went wrong');
    }
  };

  const onSubmitModal = (type, payload) => {
    switch (type) {
    case 'add':
      onAddEmail(payload);
      break;

    case 'edit':
      onEditEmail(payload);
      break;

    case 'view-staff':
    case 'view-user':
      onEditNotification(payload);
      break;

    case 'bot-telegram':
      onAddTGBot(payload);
      break;

    case 'edit-bot-telegram':
      onEditGBot(payload);
      break;

    default:
      break;
    }
  };

  useEffect(() => {
    fetchList().then((response) => {
      const findBot = response?.notifications_bots?.find((bot) => bot.bot === 0);
      if (findBot) {
        const newBot = { ...findBot, webhook_status: 0 };
        if (newBot.webhook_status === 0) {
          // eslint-disable-next-line no-use-before-define
          runStatusIntervalChecker(findBot?.id);
        }
      }
    });
    document.title = 'Notifications';

    return () => {
      clearStatusInterval();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="settings-notifications">
      <ModalsManager
        open={openModal}
        item={selectedItem?.current}
        access={access}
        error={modalError}
        success={modalSuccess}
        isLoading={isLoading.modal}
        isLoadingSubmit={isLoading.modalSubmit}
        isLoadingSubmitBot={isLoading.modalSubmitBot}
        languages={languages}
        onHide={onHideModals}
        onSubmit={onSubmitModal}
        onDelete={onDeleteEmail}
        onResend={onResendEmail}
        onReset={onReset}
        fetchPreview={fetchPreview}
      />

      {isLoading.page ? (
        <div className="loader-container" style={{ minHeight: '300px' }}>
          <Loader />
        </div>
      ) : (
        <>
          {access?.notifications_list ? (
            <>
              {access?.notifications_bots_connect_telegram && (
                <Bot showEditButton={access?.notifications_bots_edit_token_telegram} tgBot={tgBot} setOpenModal={setOpenModal} />
              )}
              <Table access={access} notifications={data?.notifications} staff={data?.staffs} onAction={onAction} onToggle={onChangeStatus} />
            </>
          ) : null}
        </>
      )}
    </div>
  );
};

export default Notifications;
